import { useState } from 'react';
import { IconCheck, IconTrashXFilled, IconX } from '@tabler/icons-react';
import DeleteIconCircle from '../components/DeleteIconCircle';
import DetallesBlue from '../Utils/DetallesBlue';

const FONT = '"Public Sans", sans-serif';

const isActionColumn = (col) => col.key === 'detalles' || col.key === 'eliminar';

const moneyKeys = ['subtotal', 'descuento', 'comision', 'iva', 'total'];

function cellTextStyle(col) {
  if (col.key === 'id') {
    return {
      fontSize: '12px',
      fontWeight: 600,
      color: 'rgba(255, 255, 255, 0.35)',
      fontVariantNumeric: 'tabular-nums',
    };
  }
  if (col.key === 'cliente') {
    return { fontSize: '15px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.92)' };
  }
  if (col.key === 'articulos') {
    return {
      fontSize: '14px',
      fontWeight: 500,
      color: 'rgba(255, 255, 255, 0.7)',
      fontVariantNumeric: 'tabular-nums',
    };
  }
  if (moneyKeys.includes(col.key)) {
    return {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(255, 255, 255, 0.85)',
      fontVariantNumeric: 'tabular-nums',
    };
  }
  if (col.key === 'fecha') {
    return { fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.5)' };
  }
  return { fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)' };
}

export default function TablaRegistroVentas({
  columns: columnsProp,
  rows,
  selectable = false,
  onDetails,
  onDelete,
  emptyComponent,
}) {
  const [selected, setSelected] = useState([]);

  const columns = columnsProp || [
    { key: 'id', label: 'ID', flex: 1, minWidth: '110px' },
    { key: 'cliente', label: 'Cliente', flex: 2, minWidth: '180px' },
    { key: 'articulos', label: 'Artículos', flex: 0.8, minWidth: '90px' },
    { key: 'tipo', label: 'Tipo', flex: 1, minWidth: '120px', badge: true },
    { key: 'metodo', label: 'Método de pago', flex: 1, minWidth: '140px', badge: true },
    { key: 'total', label: 'Recibido', flex: 1, minWidth: '100px' },
    { key: 'fecha', label: 'Fecha', flex: 1, minWidth: '110px' },
    { key: 'detalles', label: '', flex: 0, minWidth: '120px', render: (row) => <DetallesBlue onClick={() => onDetails?.(row.sale_id)} /> },
    { key: 'eliminar', label: '', flex: 0, minWidth: '50px', render: (row) => <DeleteIconCircle solid size={44} onClick={() => onDelete?.([row])} /> },
  ];

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Al filtrar la tabla, las filas ocultas no deben contar ni borrarse.
  const activeSelected = selected.filter((id) => rows.some((row) => row.id === id));

  // La tabla conserva su ancho mínimo para que las columnas no se aplasten en tablet.
  const minTableWidth =
    columns.reduce((acc, col) => acc + (parseInt(col.minWidth, 10) || 0), 0) +
    (selectable ? 48 : 0) +
    24;

  const handleBulkDelete = () => {
    const selectedRows = rows.filter((r) => activeSelected.includes(r.id));
    if (onDelete) onDelete(selectedRows);
    setSelected([]);
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-1 min-h-0 overflow-auto pedidos-scroll ${activeSelected.length > 0 ? 'pb-24' : ''}`}>
        <div style={{ minWidth: rows.length > 0 ? `${minTableWidth}px` : undefined }}>
          {/* Header */}
          <div
            className="sticky top-0 z-10 flex items-center bg-black"
            style={{ padding: '0 12px', height: '40px' }}
          >
            {selectable && <div style={{ width: '48px', flexShrink: 0 }} />}
            {columns.map((col, i) => (
              <span
                key={i}
                className={`flex items-center ${isActionColumn(col) ? 'justify-center' : 'justify-start'}`}
                style={{
                  flex: col.flex || 1,
                  minWidth: col.minWidth || 0,
                  fontSize: '11px',
                  lineHeight: '16px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  fontFamily: FONT,
                  color: 'rgba(255, 255, 255, 0.35)',
                }}
              >
                {col.label}
              </span>
            ))}
          </div>

          {/* Rows */}
          {rows.length === 0 && emptyComponent ? (
            <div className="flex items-center justify-center px-4 py-10" style={{ minHeight: '360px' }}>
              {emptyComponent}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rows.map((row) => {
                const isSelected = selected.includes(row.id);

                return (
                  <div
                    key={row.id}
                    className={`flex items-center rounded-2xl transition-colors duration-200 ease-out ${
                      isSelected ? '' : '[@media(hover:hover)]:hover:bg-white/[0.03]'
                    }`}
                    style={{
                      padding: '0 12px',
                      minHeight: '64px',
                      backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                    }}
                  >
                    {selectable && (
                      <button
                        type="button"
                        onClick={() => toggle(row.id)}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`Seleccionar venta ${row.id}`}
                        className="flex items-center justify-center shrink-0 rounded-full border-none bg-transparent cursor-pointer touch-manipulation"
                        style={{ width: '44px', height: '44px', marginRight: '4px' }}
                      >
                        <span
                          className="flex items-center justify-center rounded-full transition-colors duration-200"
                          style={{
                            width: '22px',
                            height: '22px',
                            backgroundColor: isSelected ? '#0071e3' : 'rgba(255, 255, 255, 0.08)',
                          }}
                        >
                          {isSelected && <IconCheck size={13} color="#fff" stroke={3} />}
                        </span>
                      </button>
                    )}
                    {columns.map((col, j) => (
                      <span
                        key={j}
                        className={`flex items-center min-w-0 ${
                          isActionColumn(col) ? 'justify-center' : 'justify-start'
                        }`}
                        style={{
                          flex: col.flex || 1,
                          minWidth: col.minWidth || 0,
                          fontFamily: FONT,
                          ...cellTextStyle(col),
                        }}
                      >
                        {col.render ? (
                          col.render(row)
                        ) : col.badge ? (
                          <span
                            className="inline-flex items-center justify-center"
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.06)',
                              borderRadius: '9999px',
                              height: '30px',
                              padding: '0 12px',
                              fontSize: '13px',
                              fontWeight: 600,
                              fontFamily: FONT,
                              color: 'rgba(255, 255, 255, 0.8)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row[col.key]}
                          </span>
                        ) : (
                          <span className="truncate">{row[col.key]}</span>
                        )}
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selection action bar */}
      {activeSelected.length > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full"
          style={{
            maxWidth: 'calc(100vw - 24px)',
            padding: '8px',
            backgroundColor: 'rgba(20, 20, 20, 0.94)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            boxShadow: '0 20px 48px rgba(0, 0, 0, 0.6)',
            fontFamily: FONT,
          }}
        >
          <span
            className="shrink-0 truncate"
            style={{
              padding: '0 10px',
              fontSize: '13px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {activeSelected.length === 1 ? '1 seleccionada' : `${activeSelected.length} seleccionadas`}
          </span>
          <button
            type="button"
            onClick={() => setSelected([])}
            className="flex items-center justify-center gap-1.5 shrink-0 h-11 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.1]"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: FONT,
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <IconX size={15} color="rgba(255, 255, 255, 0.6)" stroke={2} />
            Deseleccionar
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            className="flex items-center justify-center gap-1.5 shrink-0 h-11 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
            style={{
              backgroundColor: '#ff453a',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: FONT,
            }}
          >
            <IconTrashXFilled size={15} color="#fff" />
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
}
